import { apiClient } from '../utils/api-client'

/**
 * Writes banner answers to the consent register (Article 7(1) GDPR: we have to
 * be able to demonstrate consent, refusals included).
 *
 * Everything here is best-effort by design. The visitor's choice is honoured
 * locally the moment they click, and no failure to reach our server may undo
 * that — so a POST that does not land is queued and retried at the next boot
 * rather than surfaced or, worse, allowed to block the decision.
 */

const PENDING_KEY = 'divepeaks-consent-pending'

/** Bounds the queue: an offline visitor toggling repeatedly should not grow it without end. */
const MAX_PENDING = 20

export interface ConsentPayload {
  subject_id: string
  decision: string
  purposes: Record<string, boolean>
  policy_version: string
  locale: string
}

function readPending(): ConsentPayload[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writePending(entries: ConsentPayload[]) {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(PENDING_KEY)
      return
    }
    localStorage.setItem(PENDING_KEY, JSON.stringify(entries.slice(-MAX_PENDING)))
  } catch {
    // Nothing to be done: the answer still stands locally.
  }
}

async function post(payload: ConsentPayload): Promise<boolean> {
  try {
    // requireAuth is false on purpose — the banner is answered before anyone
    // signs in. The endpoint picks up a token when one happens to be there.
    // showErrors off: bookkeeping that failed is ours to retry, and a toast
    // about it would only tell the visitor their answer had not registered
    // when in fact it had.
    const response = await apiClient.post('/api/consent_records', payload, {
      requireAuth: false,
      showErrors: false,
    })
    return response.ok
  } catch {
    return false
  }
}

/** Records one answer, queueing it for the next boot if the server cannot be reached. */
export async function recordConsent(payload: ConsentPayload): Promise<void> {
  if (await post(payload)) return

  writePending([...readPending(), payload])
}

/**
 * Retries whatever earlier visits could not deliver. Entries that fail again go
 * back in the queue; the register is allowed to lag, not to lose.
 */
export async function flushPendingConsent(): Promise<void> {
  const pending = readPending()
  if (pending.length === 0) return

  // Cleared up front so a flush racing a fresh answer cannot rewrite the queue
  // with a stale copy of itself; anything still failing is put back below.
  writePending([])

  const failed: ConsentPayload[] = []
  for (const entry of pending) {
    if (!(await post(entry))) failed.push(entry)
  }

  if (failed.length > 0) writePending([...failed, ...readPending()])
}
